import { SettingsV2, SupportedLanguages } from '@dixa/messenger-api-payloads';
import { AvailableLocales, MessengerV2 } from 'lib-shared-legacy-types';
import { UnionValuesWith } from 'lib-shared-types-utils';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';

import {
  isMessengerTranslatedLanguage,
  MessengerTranslatedLanguage,
} from 'components/settings/components/sections/dixaMessenger/views/appearance/components/generalSettings/languageSettings/languages';
import { FormValues } from 'components/settings/components/sections/dixaMessenger/views/appearance/types';

import { KnowledgeState } from '../../messengerSettings.types';

type AllAvailableLocales = SupportedLanguages;
type PatchSettings = SettingsV2.PatchSettings;
const defaultAvailableLocales: MessengerTranslatedLanguage[] = ['en'];

const emptySettings: FormValues = {
  backgroundColor: 'purple',
  title: {},
  description: {},
  startConversation: {},
  findArticles: {},
  bulletinBoardId: null,
  bulletinBoardEnabled: false,
  availableLocales: defaultAvailableLocales,
  defaultLanguage: defaultAvailableLocales[0],
  logo: undefined,
  launcherIconType: 'dixa-branded-default',
  minimumIdentificationLevel: 'ANONYMOUS',
  agentDisplayNameFirstWord: false,
};

export function formatFormValues(
  values: FormValues = emptySettings,
  knowledgeState: KnowledgeState,
): PatchSettings {
  const baseSettings: PatchSettings = {
    version: 2,

    style: {
      theme: {
        background: values.backgroundColor,
      },

      icon:
        values.launcherIconType === 'chat-bubbles'
          ? {
              type: 'preset',
              id: 'unbranded-default-icon',
            }
          : {
              type: 'preset',
              id: 'branded-dixa-icon',
            },
    },

    general: {
      title: values.title,

      description: values.description,

      startConversation: values.startConversation,

      findArticles: values.findArticles,

      availableLocales: putDefaultLanguageAsFirstItemInAvailableLocale(
        values.availableLocales || emptySettings.availableLocales,
        values.defaultLanguage,
      ),

      minimumIdentificationLevel: values.minimumIdentificationLevel,

      logo: values.logo
        ? {
            type: 'fixed',
            fixed: { fileId: values.logo },
          }
        : null,
      agent: {
        nameDisplay: values.agentDisplayNameFirstWord
          ? 'display-name-first-word'
          : 'display-name-full',
      },
    },

    integrations: {},
  };

  if (values.bulletinBoardId) {
    baseSettings.integrations = {
      bulletinBoard: {
        boardId: values.bulletinBoardId,
        enabled: values.bulletinBoardEnabled ?? false,
      },
    };
  }

  return updateWithKnowledgeState(baseSettings, knowledgeState);
}

export function extractInitialValuesFromMessenger(
  messenger: MessengerV2 | null,
  fallbackSettings?: SettingsV2.Settings,
): Partial<FormValues> {
  if (messenger === null) {
    return {};
  }

  const { settings } = messenger;

  const values: UnionValuesWith<FormValues, undefined> = {
    title: {
      ...fallbackSettings?.general?.title,
      ...(settings?.general?.title || {}),
    },

    description: {
      ...fallbackSettings?.general?.description,
      ...(settings?.general?.description || {}),
    },

    startConversation: {
      ...fallbackSettings?.general?.startConversation,
      ...(settings?.general?.startConversation || {}),
    },

    findArticles: {
      ...fallbackSettings?.general?.findArticles,
      ...(settings?.general?.findArticles || {}),
    },

    backgroundColor:
      settings?.style?.theme?.background ||
      fallbackSettings?.style?.theme?.background,

    bulletinBoardId: settings?.integrations?.bulletinBoard?.boardId,

    bulletinBoardEnabled:
      settings?.integrations?.bulletinBoard?.enabled ?? false,

    availableLocales: filterOutUntranslatedLanguages(
      settings?.general?.availableLocales ||
        fallbackSettings?.general?.availableLocales,
    ),

    defaultLanguage:
      filterOutUntranslatedLanguages(settings?.general?.availableLocales)[0] ??
      'en',

    minimumIdentificationLevel:
      settings?.general?.minimumIdentificationLevel ||
      fallbackSettings?.general?.minimumIdentificationLevel ||
      'ANONYMOUS',

    logo: settings?.general?.logo?.fixed?.fileId,

    launcherIconType:
      settings?.style?.icon?.type === 'preset' &&
      settings?.style?.icon?.id === 'unbranded-default-icon'
        ? 'chat-bubbles'
        : 'dixa-branded-default',
    agentDisplayNameFirstWord:
      settings?.general?.agent?.nameDisplay === 'display-name-first-word',
  };

  return omitBy(values, isUndefined);
}

function putDefaultLanguageAsFirstItemInAvailableLocale(
  availableLocales: AvailableLocales,
  defaultLanguage: SupportedLanguages,
): AvailableLocales {
  if (availableLocales.length === 0) {
    return [defaultLanguage];
  }

  if (availableLocales.length === 1) {
    return availableLocales;
  }

  // Use a `Set` to remove the potential duplicates
  return [...new Set([defaultLanguage, ...availableLocales])];
}

/**
 * There are some languages that are defined in the payloads library as a part
 * of the supported languages which are not yet translated on the widget side.
 * To keep the type system happy and prevent any potential bugs, this function
 * removes those untranslated languages from the list.
 */
export function filterOutUntranslatedLanguages(
  availableLocales: AllAvailableLocales[] | undefined = [],
): MessengerTranslatedLanguage[] {
  return availableLocales.filter(isMessengerTranslatedLanguage);
}

function updateWithKnowledgeState(
  formattedValues: PatchSettings,
  knowledgeState: KnowledgeState,
): PatchSettings {
  if (knowledgeState.isPristine === false) {
    if (knowledgeState.elevioIntegration.type === 'not-connected') {
      formattedValues.integrations.elevio = undefined;
    } else {
      formattedValues.integrations.elevio = {
        companyId: knowledgeState.elevioIntegration.companyId,
        enabled: knowledgeState.elevioIntegration.enabled,
        displayType: knowledgeState.elevioIntegration.displayType,
        searchBarPlaceholder:
          knowledgeState.elevioIntegration.translations.searchBarPlaceholder
            .translatedMap,
      };

      formattedValues.general.findArticles =
        knowledgeState.elevioIntegration.translations.findArticles.translatedMap;
    }
  }

  return formattedValues;
}
